import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AWS from 'aws-sdk';
import Layout from '../components/Layout';
import '../styles/conversational-agents.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const ConversationalAgents = ({ user }) => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    useCase: ''
  });
  const [submitStatus, setSubmitStatus] = useState('');
  
  const isAuthenticated = user !== null;

  if (!isAuthenticated) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in to access conversational agent services.</p>
        </div>
      </Layout>
    );
  }

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactInfo.name || !contactInfo.email || !contactInfo.useCase) {
      setSubmitStatus('Please provide your name, email address, and use case.');
      return;
    }
    
    setSubmitStatus('Submitting...');
    
    try {
      // Get AWS credentials from Amplify Auth
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      // Configure AWS SDK with proper credentials
      AWS.config.update({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });
      
      // Send email notification
      const ses = new AWS.SES();
      const emailParams = {
        Source: 'info@relufox.ai',
        Destination: {
          ToAddresses: ['info@relufox.ai']
        },
        Message: {
          Subject: {
            Data: 'New Conversational Agent Inquiry - Fox River AI'
          },
          Body: {
            Text: {
              Data: `A new inquiry has been submitted for conversational agent services:\n\nCONTACT INFORMATION:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company || 'Not provided'}\nPhone: ${contactInfo.phone || 'Not provided'}\n\nUSE CASE:\n${contactInfo.useCase}\n\nPlease follow up with this customer.`
            }
          }
        }
      };
      
      await ses.sendEmail(emailParams).promise();
      
      setSubmitStatus('Thank you! Your inquiry has been submitted. We will contact you within 24 hours.');
      setContactInfo({
        name: '',
        email: '',
        company: '',
        phone: '',
        useCase: ''
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus(`Submission failed: ${error.message || 'Unknown error'}. Please try again or contact us directly.`);
    }
  };

  return (
    <Layout>
      <div className="conversational-agents-page">
        <div className="hero-section">
          <h1>Conversational AI Agents</h1>
          <p className="hero-subtitle">Custom AI assistants that understand your business, data, and customers</p>
        </div>

        <div className="content-container">
          <section className="service-overview">
            <h2>Transform How You Engage</h2>
            <p className="section-intro">Our conversational agents go beyond simple chatbots. We build AI assistants that understand your specific business context, can access your proprietary data, and deliver meaningful interactions with customers and employees.</p>
            
            <div className="services-grid">
              <div className="service-item">
                <h3>Document-Aware Agents</h3>
                <p>AI assistants that can read, understand, and answer questions about your documents, manuals, and knowledge bases</p>
              </div>
              <div className="service-item">
                <h3>Customer Support Automation</h3>
                <p>Reduce response times and support costs while maintaining high-quality customer interactions</p>
              </div>
              <div className="service-item">
                <h3>Internal Knowledge Assistants</h3>
                <p>Help employees quickly find information across your organization's data</p>
              </div>
              <div className="service-item">
                <h3>Multi-Step Workflows</h3>
                <p>Agents that can guide users through complex processes and transactions</p>
              </div>
            </div>
          </section>

          <section className="technology-section">
            <h2>Advanced Technology</h2>
            <div className="tech-features">
              <div className="tech-item">
                <h4>🔍 Retrieval-Augmented Generation (RAG)</h4>
                <p>Our agents search through your data to provide accurate, contextual responses</p>
              </div>
              <div className="tech-item">
                <h4>🧠 Fine-Tuned Models</h4>
                <p>Custom-trained AI that understands your industry terminology and processes</p>
              </div>
              <div className="tech-item">
                <h4>🔒 Private Deployment Options</h4>
                <p>On-premises solutions for sensitive data that never leaves your network</p>
              </div>
              <div className="tech-item">
                <h4>🔄 Continuous Learning</h4>
                <p>Agents that improve over time based on interactions and feedback</p>
              </div>
            </div>
          </section>

          <section className="use-cases">
            <h2>Real-World Applications</h2>
            <div className="use-case-grid">
              <div className="use-case-item">
                <h3>Customer Service</h3>
                <p>24/7 support that can handle 80% of common inquiries without human intervention</p>
              </div>
              <div className="use-case-item">
                <h3>Employee Onboarding</h3>
                <p>AI assistants that help new hires navigate policies, procedures, and training materials</p>
              </div>
              <div className="use-case-item">
                <h3>Sales Support</h3>
                <p>Product experts that can answer detailed questions and guide purchase decisions</p>
              </div>
              <div className="use-case-item">
                <h3>Data Analysis</h3>
                <p>Natural language interfaces to your databases and analytics tools</p>
              </div>
            </div>
          </section>

          <section className="inquiry-section">
            <h2>Start Building Your Custom Agent</h2>
            <p>Tell us about your use case, and we'll schedule a consultation to discuss how a conversational agent can help your business.</p>
            
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Full Name *" 
                  value={contactInfo.name}
                  onChange={(e) => handleContactInfoChange('name', e.target.value)}
                  className="form-input"
                  required
                />
                <input 
                  type="email" 
                  placeholder="Email Address *" 
                  value={contactInfo.email}
                  onChange={(e) => handleContactInfoChange('email', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Company (Optional)" 
                  value={contactInfo.company}
                  onChange={(e) => handleContactInfoChange('company', e.target.value)}
                  className="form-input"
                />
                <input 
                  type="tel" 
                  placeholder="Phone (Optional)" 
                  value={contactInfo.phone}
                  onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                  className="form-input"
                />
              </div>
              <textarea
                placeholder="Describe your use case or what you'd like your AI assistant to do *"
                value={contactInfo.useCase}
                onChange={(e) => handleContactInfoChange('useCase', e.target.value)}
                className="form-textarea"
                rows="5"
                required
              ></textarea>
              <button type="submit" className="submit-btn">
                Request Consultation
              </button>
              {submitStatus && <p className="submit-status">{submitStatus}</p>}
            </form>
            
            <div className="contact-alternative">
              <p>Prefer to discuss your needs directly?</p>
              <a href="tel:6309408837" className="contact-btn">Call (630) 940-8837</a>
              <p>or email us at <a href="mailto:info@relufox.ai">info@relufox.ai</a></p>
            </div>
          </section>

          <section className="process-section">
            <h2>Our Development Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Discovery</h3>
                <p>We analyze your needs, data sources, and integration requirements</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Design & Training</h3>
                <p>Custom agent design and training on your specific domain knowledge</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Development</h3>
                <p>Building the agent with RAG capabilities and integration with your systems</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Deployment & Optimization</h3>
                <p>Launch, monitor, and continuously improve your agent's performance</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default ConversationalAgents;